// src/components/crm/SessionSelectionModal.js
import React, { useState, useEffect } from 'react';
import { getSessionsDisponiblesBatch } from '../../services/infoCentre/session_formation_service.js';

/**
 * Choix des sessions d'un étudiant, formation par formation.
 *
 * Plusieurs sessions par formation : un BTS se suit sur plusieurs sessions
 * (modules) en parallèle, et l'étudiant doit être inscrit à toutes pour voir
 * son emploi du temps complet.
 *
 * - `sessionsActuelles` (facultatif) : { [formationId]: [{ id, libelle }] },
 *   les sessions où l'étudiant est déjà inscrit. Elles sont cochées, et
 *   proposées même si elles ne sont plus « disponibles » (terminées…), pour
 *   qu'enregistrer ne les retire pas en silence.
 * - `onConfirm` reçoit [{ formation_id, session_id }], un élément par session.
 */
const dateCourte = (valeur) => {
    if (!valeur) return '';
    const d = new Date(String(valeur).replace(' ', 'T'));
    return Number.isNaN(d.getTime()) ? String(valeur) : d.toLocaleDateString('fr-FR');
};

const libelleSession = (s) =>
    `${s.intitule_session} — ${dateCourte(s.date_debut)} → ${dateCourte(s.date_fin)}`
    + (s.mode_display ? ` (${s.mode_display})` : '');

const SessionSelectionModal = ({ isOpen, onClose, onConfirm, formations, prospectNom, sessionsActuelles }) => {
    const [sessionsData, setSessionsData] = useState({});
    const [loading, setLoading] = useState(true);
    const [selectedSessions, setSelectedSessions] = useState({});
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (isOpen && formations && formations.length > 0) {
            loadSessions();
        }
    }, [isOpen, formations]); // eslint-disable-line react-hooks/exhaustive-deps

    const loadSessions = async () => {
        setLoading(true);
        try {
            const formationIds = formations.map(f => f.id);
            const data = await getSessionsDisponiblesBatch(formationIds);

            // Options : sessions disponibles + sessions actuelles absentes de la liste.
            const options = {};
            const initialSelected = {};
            formations.forEach(f => {
                const disponibles = (data[f.id] || []).map(s => ({ id: s.id, libelle: libelleSession(s) }));
                const actuelles = sessionsActuelles?.[f.id] || [];
                const connues = new Set(disponibles.map(s => s.id));
                options[f.id] = [...disponibles, ...actuelles.filter(s => !connues.has(s.id))];

                if (actuelles.length) initialSelected[f.id] = actuelles.map(s => s.id);
                else if (disponibles.length) initialSelected[f.id] = [disponibles[0].id];
                else initialSelected[f.id] = [];
            });
            setSessionsData(options);
            setSelectedSessions(initialSelected);
        } catch (error) {
            console.error('Erreur chargement sessions:', error);
        } finally {
            setLoading(false);
        }
    };

    const basculer = (formationId, sessionId) => {
        setSelectedSessions(prev => {
            const actuelles = prev[formationId] || [];
            return {
                ...prev,
                [formationId]: actuelles.includes(sessionId)
                    ? actuelles.filter(id => id !== sessionId)
                    : [...actuelles, sessionId],
            };
        });
        setErrors(prev => ({ ...prev, [formationId]: null }));
    };

    const toutCocher = (formationId) => {
        setSelectedSessions(prev => ({ ...prev, [formationId]: (sessionsData[formationId] || []).map(s => s.id) }));
        setErrors(prev => ({ ...prev, [formationId]: null }));
    };

    const handleConfirm = () => {
        // Au moins une session par formation
        const newErrors = {};
        formations.forEach(f => {
            if (!(selectedSessions[f.id] || []).length) {
                newErrors[f.id] = 'Cochez au moins une session';
            }
        });

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        onConfirm(formations.flatMap(f =>
            selectedSessions[f.id].map(session_id => ({ formation_id: f.id, session_id }))
        ));
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay show" onClick={onClose}>
            <div className="modal-session-selection" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>
                        <i className="fa-solid fa-calendar-alt"></i>
                        Sélectionner les sessions
                    </h2>
                    <button className="modal-close" onClick={onClose}>
                        <i className="fa-solid fa-xmark"></i>
                    </button>
                </div>

                <div className="modal-body">
                    <div className="session-selection-info">
                        <i className="fa-solid fa-info-circle"></i>
                        <span>Pour chaque formation, cochez la ou les sessions auxquelles {prospectNom} sera inscrit(e).</span>
                    </div>

                    {loading ? (
                        <div className="session-loading">
                            <i className="fa-solid fa-spinner fa-spin"></i>
                            <p>Chargement des sessions disponibles...</p>
                        </div>
                    ) : (
                        <div className="session-forms-list">
                            {formations.map(formation => {
                                const sessions = sessionsData[formation.id] || [];
                                const cochees = selectedSessions[formation.id] || [];
                                const hasError = errors[formation.id];

                                return (
                                    <div key={formation.id} className="session-formation-card">
                                        <div className="session-formation-header">
                                            <i className="fa-solid fa-graduation-cap"></i>
                                            <span className="session-formation-name">{formation.label}</span>
                                            <span className="session-formation-duree">{formation.duree}</span>
                                        </div>

                                        <div className={`session-select-group ${hasError ? 'has-error' : ''}`}>
                                            <div className="session-choix-entete">
                                                <label>
                                                    <i className="fa-solid fa-calendar-week"></i>
                                                    Sessions ({cochees.length} cochée{cochees.length > 1 ? 's' : ''})
                                                </label>
                                                {sessions.length > 1 && cochees.length < sessions.length && (
                                                    <button type="button" className="session-tout-cocher"
                                                            onClick={() => toutCocher(formation.id)}>
                                                        Tout cocher
                                                    </button>
                                                )}
                                            </div>
                                            <div className="session-choix-liste">
                                                {sessions.map(session => (
                                                    <label key={session.id} className="session-choix">
                                                        <input
                                                            type="checkbox"
                                                            checked={cochees.includes(session.id)}
                                                            onChange={() => basculer(formation.id, session.id)}
                                                        />
                                                        <span>{session.libelle}</span>
                                                    </label>
                                                ))}
                                            </div>
                                            {hasError && <span className="error-message">{hasError}</span>}
                                            {sessions.length === 0 && (
                                                <div className="no-session-warning">
                                                    <i className="fa-solid fa-triangle-exclamation"></i>
                                                    Aucune session disponible pour cette formation
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                <div className="modal-footer">
                    <button className="btn btn-cancel" onClick={onClose}>
                        Annuler
                    </button>
                    <button
                        className="btn btn-confirm"
                        onClick={handleConfirm}
                        disabled={loading}
                    >
                        <i className="fa-solid fa-check"></i>
                        Confirmer les sessions
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SessionSelectionModal;
